<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { ContentsContext } from '@/api/tracking';
import { EMPTY_KEYWORD_FEED_MESSAGE } from '@/constants';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import BasicEmptyFeed from '@/components/common/basic-empty-feed.vue';
import PersonalFeedCardSkeleton from '@/components/home/personal-feed-card-skeleton.vue';
import { AdpDocument, Article, ExecutionInformation } from '@/types';
import { syncAdpDocumentActions } from '@/utils/adpDocument';
import { useAdpDocumentOptionMenu } from '@/utils/composables/useAdpDocumentOptionMenu';
import { useEmitter } from '@/utils/vue';

type Props = {
  executionInfo: ExecutionInformation;
  domesticArticles?: Article[];
  foreignArticles?: Article[];
  previousTotalFeedCount: number;
  isLoaded: boolean;
  contentsContext?: ContentsContext;
};
const props = withDefaults(defineProps<Props>(), {
  domesticArticles: () => [],
  foreignArticles: () => [],
});

const emitter = useEmitter();

const executionTime = props.executionInfo.executionTime;

const removedArticleIds = ref([] as number[]);
emitter.on('personal-feed-article-removed', (article: Article) => {
  removedArticleIds.value.push(article.id);
});
emitter.on('personal-feed-article-restored', (article: Article) => {
  removedArticleIds.value = removedArticleIds.value.filter(
    articleId => articleId !== article.id,
  );
});

const syncArticle = (updated: AdpDocument) => {
  syncAdpDocumentActions(props.domesticArticles, updated);
  syncAdpDocumentActions(props.foreignArticles, updated);
};
emitter.on('article-updated', syncArticle);
emitter.on('article-viewed', syncArticle);
onUnmounted(() => {
  emitter.off('article-updated', syncArticle);
  emitter.off('article-viewed', syncArticle);
});

const visibleDomesticArticles = computed(() =>
  props.domesticArticles.filter(a => !removedArticleIds.value.includes(a.id)),
);
const visibleForeignArticles = computed(() =>
  props.foreignArticles.filter(a => !removedArticleIds.value.includes(a.id)),
);
const articleCount = computed(
  () =>
    visibleDomesticArticles.value.length + visibleForeignArticles.value.length,
);
const SKELETON_COUNT = 6;

const optionMenu = useAdpDocumentOptionMenu({
  pageName: 'home',
  feature: 'personal_feed',
  enablePersonalFeedSiteExclusion: true,
});
const personalFeedConfig = { notInterested: true, excludeMedia: true };
</script>

<template>
  <div>
    <div class="execution-title c-title-bold--s">{{ executionTime }}更新</div>
    <div class="articles">
      <template v-if="!isLoaded">
        <PersonalFeedCardSkeleton v-for="n in SKELETON_COUNT" :key="n" />
      </template>
      <template v-else-if="articleCount > 0">
        <div
          class="personal-feed-article"
          v-for="article in visibleDomesticArticles"
          :key="article.id"
        >
          <div class="divider"></div>
          <AdpDocumentCard
            page-name="home"
            mark-source="personal_feed"
            feature="personal_feed"
            feed-type="domestic"
            :adp-document="article"
            :show-comment-count="1"
            :disable-related-articles="false"
            :execution="executionInfo.execution"
            width="100%"
            :option-actions="
              optionMenu.buildOptionActions(article, personalFeedConfig)
            "
            :contents-context="contentsContext"
            @option-action="
              name => optionMenu.handleOptionAction(name, article)
            "
          ></AdpDocumentCard>
        </div>
        <div
          class="personal-feed-article"
          v-for="article in visibleForeignArticles"
          :key="article.id"
        >
          <div class="divider"></div>
          <AdpDocumentCard
            page-name="home"
            mark-source="personal_feed"
            feature="personal_feed"
            feed-type="foreign"
            :adp-document="article"
            :show-comment-count="1"
            :disable-related-articles="false"
            :execution="executionInfo.execution"
            width="100%"
            :option-actions="
              optionMenu.buildOptionActions(article, personalFeedConfig)
            "
            :contents-context="contentsContext"
            @option-action="
              name => optionMenu.handleOptionAction(name, article)
            "
          ></AdpDocumentCard>
        </div>
        <div class="footer">
          <router-link :to="{ name: 'personalNews' }">
            <button
              class="c-btn c-btn--small c-btn--auto c-btn--outline c-text-regular--m"
            >
              もっと見る
            </button>
          </router-link>
        </div>
      </template>
      <div v-else>
        <div class="divider"></div>
        <BasicEmptyFeed>{{ EMPTY_KEYWORD_FEED_MESSAGE }}</BasicEmptyFeed>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.execution-title {
  padding-top: 8px;
}
.articles {
  min-height: 140px;
}
.personal-feed-article {
  .document-card {
    border: 0;
    padding: 0;
  }
}
.divider {
  width: auto;
  border-bottom: 0.5px solid $color-gray400;
  margin: 8px 0 14px 0;
}
.footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
  .c-btn {
    margin-top: 0;
  }
}
</style>
